import type {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  PopupRequest,
  RedirectRequest,
  SilentRequest
} from '@azure/msal-browser'

type TokenRequest = SilentRequest | RedirectRequest | PopupRequest
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  LogLevel,
  BrowserAuthError,
  PublicClientApplication,
  ClientConfigurationError
} from '@azure/msal-browser'
import type { AccessToken, TokenCredential } from '@azure/identity'
import type { Ref, ComputedRef } from 'vue'
import { getCurrentInstance, ref, computed, watch, toRefs } from 'vue'
import { capturePosthogEvent, toErrorMessage } from 'src/plugins/posthog-events'

const tenantName = `${import.meta.env.APP_TENANT_NAME}`
const clientId = `${import.meta.env.APP_CLIENT_ID}`
const authorityMetadata = import.meta.env.APP_B2C_METADATA_PULLED

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/B2C_1A_GLOBAL_signup_signin`,
    knownAuthorities: [`${tenantName}.b2clogin.com`],
    // The app is served at /app/ - '/' is the Astro marketing site, which never
    // calls handleRedirectPromise(), so the redirect has to land inside the app.
    // Every host's /app/ URL must be registered as a redirect URI on the Entra
    // ID (B2C) app registration.
    redirectUri: `${globalThis.location.origin}/app/`,
    ...(typeof authorityMetadata === 'string' &&
      authorityMetadata?.length > 0 && { authorityMetadata })
  },
  cache: {
    cacheLocation: 'localStorage'
  },
  system: {
    allowRedirectInIframe: true,
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            capturePosthogEvent('MSAL Error', { message })
            console.error(message)
            return
          /* case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return */
          case LogLevel.Warning:
            capturePosthogEvent('MSAL Warning', { message })
            console.warn(message)
        }
      },
      logLevel: LogLevel.Warning
    }
  }
}

export const msalInstance = new PublicClientApplication(msalConfig)

export const loginRequest = {
  scopes: ['openid']
}
export const apiRequest = {
  scopes: ['openid', `https://${tenantName}.onmicrosoft.com/${clientId}/DigitalTwins.Read.Write`]
}

export interface MsalContext {
  instance: PublicClientApplication
  accounts: Ref<AccountInfo[]>
  account: Ref<AccountInfo>
  inProgress: Ref<InteractionStatus>
}

export const useMsal = (): MsalContext => {
  const internalInstance = getCurrentInstance()
  if (!internalInstance) {
    throw new Error('useMsal() cannot be called outside the setup() function of a component')
  }
  const { instance, accounts, account, inProgress } = toRefs(
    internalInstance.appContext.config.globalProperties.$msal
  )

  if (!instance.value || !accounts.value || !inProgress.value) {
    throw new Error('Please install the msalPlugin')
  }

  if (inProgress?.value === InteractionStatus.Startup) {
    instance.value.initialize().then(() => {
      instance.value.handleRedirectPromise().catch(() => {
        // Errors should be handled by listening to the LOGIN_FAILURE event
        return
      })
    })
  }

  return {
    instance: instance.value,
    accounts,
    account,
    inProgress
  }
}

export interface MsalRolesResult {
  roles: Ref<string[]>
  envRoles: ComputedRef<{ [envId: string]: string }>
}
export const useMsalRoles = (): MsalRolesResult => {
  const { accounts } = useMsal()
  const roles = ref(accounts.value.flatMap((account) => account.idTokenClaims?.roles as string[]))

  watch(accounts, () => {
    roles.value = accounts.value.flatMap((account) => account.idTokenClaims?.roles as string[])
  })

  const envRoles = computed(() =>
    roles.value.reduce((acc: { [envId: string]: string }, role) => {
      if (role?.includes('.')) {
        const [envId, roleId] = role.split('.')
        acc[envId] = roleId
      }
      return acc
    }, {})
  )

  return {
    roles,
    envRoles
  }
}

export const useIsAuthenticated = (): Ref<boolean> => {
  const { accounts } = useMsal()
  const isAuthenticated = ref(accounts.value.length > 0)

  watch(accounts, () => {
    isAuthenticated.value = accounts.value.length > 0
  })

  return isAuthenticated
}

export const isAuthenticated = () => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]
  return !!account
}

/**
 * Extract token from URL query parameter
 * Used for service principal/automation authentication bypass
 * @returns The token string or null if not present
 */
const getTokenFromQuery = (): string | null => {
  const urlParams = new URLSearchParams(globalThis.location.search)
  return urlParams.get('token')
}

/**
 * Check if a valid token query parameter is present
 * Used by auth guard to bypass interactive authentication
 */
export const hasTokenQueryParam = (): boolean => {
  const token = getTokenFromQuery()
  // Basic JWT structure validation: three base64url-encoded parts separated by dots
  if (!token || token?.length === 0) return false

  const parts = token.split('.')
  return parts.length === 3 && parts.every((part) => part?.length > 0)
}

export const login = async (
  request: RedirectRequest | PopupRequest = loginRequest
): Promise<AccountInfo | null> => {
  const urlParams = new URLSearchParams(globalThis.location.search)
  const sid = urlParams.get('sid') || undefined
  const loginHint = urlParams.get('login_hint') || undefined
  try {
    await msalInstance.ssoSilent({
      ...request,
      loginHint,
      sid
    })
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
      try {
        if (globalThis.self === window.top) {
          await msalInstance.loginRedirect(request)
        } else {
          // Use popup when in iframe
          await msalInstance.loginPopup({
            ...request,
            // Redirect to a blank page in popup
            redirectUri: `${globalThis.location.origin}/app/blank`
          })
        }
      } catch (e) {
        capturePosthogEvent('LoginFailure', { error: toErrorMessage(e) })
        console.error('MSAL login error: ', e)
        return null
      }
    } else {
      capturePosthogEvent('LoginFailure', { error: toErrorMessage(err) })
      console.error('MSAL ssoSilent error: ', err)
      return null
    }
  }

  // Return the account after successful login
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null
}

const createAutomationToken = (queryToken: string): AuthenticationResult => ({
  accessToken: queryToken,
  account: {
    localAccountId: 'automation',
    homeAccountId: 'automation',
    environment: 'automation',
    tenantId: '',
    username: 'automation'
  },
  authority: '',
  uniqueId: 'automation',
  tenantId: '',
  scopes: [],
  idToken: '',
  idTokenClaims: {},
  tokenType: 'Bearer',
  expiresOn: new Date(Date.now() + 3600000),
  state: '',
  correlationId: '',
  fromCache: false
})

const resolveEnvironmentMismatch = (account: AccountInfo, requestScopes: string[]): boolean => {
  const accountEnvironment = account.environment
  const scopeEnvironments = requestScopes
    .filter((scope) => scope?.includes('://'))
    .map((scope) => {
      try {
        const url = new URL(scope)
        return url.hostname
      } catch {
        return null
      }
    })
    .filter(Boolean)

  return (
    scopeEnvironments.length > 0 &&
    !scopeEnvironments.some(
      (scopeEnv) => accountEnvironment?.split('.')[0] === scopeEnv?.split('.')[0]
    )
  )
}

const handleRedirectOrPopup = async (request: TokenRequest): Promise<null> => {
  try {
    if (globalThis.self === window.top) {
      await msalInstance.acquireTokenRedirect(request)
    } else {
      await msalInstance.loginPopup({
        ...request,
        redirectUri: `${globalThis.location.origin}/app/blank`
      })
    }
  } catch (innerError) {
    capturePosthogEvent('MSAL AcquireTokenFailure', { error: toErrorMessage(innerError) })
    console.error('MSAL acquireToken error: ', innerError)
  }
  return null
}

const acquireTokenWithFallback = async (
  request: TokenRequest,
  account: AccountInfo
): Promise<AuthenticationResult | null> => {
  try {
    const authResult = await msalInstance.acquireTokenSilent({
      ...request,
      account,
      forceRefresh: false
    })
    return authResult ?? null
  } catch (e) {
    if (
      e instanceof InteractionRequiredAuthError ||
      e instanceof BrowserAuthError ||
      e instanceof ClientConfigurationError
    ) {
      return handleRedirectOrPopup(request)
    }

    capturePosthogEvent('MSAL AcquireTokenSilentFailure', { error: toErrorMessage(e) })
    console.error('MSAL acquireTokenSilent error: ', e)
    return null
  }
}

export const getApiToken = async (
  request: TokenRequest = apiRequest
): Promise<AuthenticationResult | null> => {
  const queryToken = getTokenFromQuery()
  if (queryToken) {
    return createAutomationToken(queryToken)
  }

  let account: AccountInfo | null =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]

  if (account && request.scopes && resolveEnvironmentMismatch(account, request.scopes)) {
    console.warn('Environment mismatch detected, forcing new login', {
      accountEnvironment: account.environment,
      scopes: request.scopes
    })
    msalInstance.setActiveAccount(null)
    account = null
  }

  if (!account) {
    account = await login(request)
    if (!account) return null
  }

  return acquireTokenWithFallback(request, account)
}

/** TokenCredential compatible with core-http */
export const tokenCredential: TokenCredential = {
  getToken: async (): Promise<AccessToken | null> => {
    const authResponse = await getApiToken()
    if (!authResponse?.expiresOn) return null
    return {
      token: authResponse.accessToken,
      expiresOnTimestamp: authResponse.expiresOn.getTime() / 1000
    }
  }
}

/**
 * Token provider for Kusto client
 * @returns The access token or null if not authenticated
 */
export const tokenProvider = async (): Promise<string> => {
  const authResponse = await getApiToken()
  return authResponse?.accessToken || ''
}
